import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Chip, TextField, InputAdornment,
  CircularProgress, Collapse, IconButton, Tooltip, Alert, Divider,
  LinearProgress, ToggleButton, ToggleButtonGroup, Grid, Card, CardContent,
  Dialog, DialogContent, DialogTitle,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import RecommendIcon from '@mui/icons-material/Recommend';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Area, AreaChart, BarChart, Bar, Legend,
  PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis,
  Treemap, ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  FunnelChart, Funnel, LabelList,
} from 'recharts';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import {
  buildInsightsSummary, buildForecastSummary, buildCustomerSummary,
  buildQueryContext, buildRecommendationContext, hashKey,
} from './salesDashboardAIUtils';

// Convert markdown-style formatting to inline HTML for readable display
const formatMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(99,102,241,0.12);padding:1px 4px;border-radius:3px;font-size:12px">$1</code>')
    .replace(/\n/g, '<br/>');
};

// Export chat data as CSV/Excel download
const exportToCSV = (data, chartType, question) => {
  if (!data?.length) return;
  let csv = '';
  if (chartType === 'scatter') {
    csv = 'Label,X,Y\n' + data.map(d => `"${d.label || ''}",${d.x},${d.y}`).join('\n');
  } else if (chartType === 'multi_table' && data[0]?.columns) {
    const cols = data[0].columns;
    csv = cols.join(',') + '\n' + data.map(row => cols.map(c => `"${row[c] ?? ''}"`).join(',')).join('\n');
  } else if (chartType === 'multi_series' && data[0] && Object.keys(data[0]).length > 2) {
    const keys = Object.keys(data[0]);
    csv = keys.join(',') + '\n' + data.map(row => keys.map(k => `"${row[k] ?? ''}"`).join(',')).join('\n');
  } else {
    csv = 'Label,Value\n' + data.map(d => `"${d.label || d.name || ''}",${d.value ?? ''}`).join('\n');
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  a.href = url;
  a.download = `sales-ai-${ts}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const copyTextToClipboard = (text) => {
  navigator.clipboard.writeText(text).catch(() => {});
};

const CHART_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#818cf8', '#7c3aed', '#5b21b6', '#4f46e5', '#4338ca', '#3730a3', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#ec4899'];

// Ensure API response is a parsed object, not a string or Response
const ensureParsed = async (data) => {
  if (data instanceof Response) { try { data = await data.json(); } catch { return {}; } }
  if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return { rawText: data }; } }
  // Handle double-encoded: response.json() returned a string
  if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return { rawText: data }; } }
  return data || {};
};

const AI_COLOR = '#8b5cf6'; // Purple for AI features
const SEGMENT_COLORS = { VIP: '#f59e0b', Growing: '#10b981', 'At-Risk': '#f97316', Dormant: '#6b7280', New: '#3b82f6' };
const SEVERITY_CONFIG = {
  positive: { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, color: '#10b981', bg: '#10b98115' },
  info: { icon: <InfoIcon sx={{ fontSize: 18 }} />, color: '#3b82f6', bg: '#3b82f615' },
  warning: { icon: <WarningAmberIcon sx={{ fontSize: 18 }} />, color: '#f59e0b', bg: '#f59e0b15' },
  critical: { icon: <ErrorIcon sx={{ fontSize: 18 }} />, color: '#ef4444', bg: '#ef444415' },
};

// =====================================================================
// AI Insights Panel
// =====================================================================
export const InsightsPanel = ({ filteredData, getItemInfo, dateFrom, dateTo, source }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(true);
  const [expandedInsight, setExpandedInsight] = useState(null); // index of expanded insight
  const [maximized, setMaximized] = useState(false);
  const [history, setHistory] = useState([]); // Array of { dateRange, source, insights, timestamp }
  const [showHistory, setShowHistory] = useState(false);
  const cache = useRef({});

  const analyze = useCallback(async () => {
    const key = hashKey(`insights_${filteredData.length}_${dateFrom}_${dateTo}_${source}`);
    if (cache.current[key]) { setInsights(cache.current[key]); return; }
    setLoading(true); setError(null);
    try {
      const summary = buildInsightsSummary(filteredData, getItemInfo, dateFrom, dateTo);
      let data = await ensureParsed(await apiCall(API_ENDPOINTS.SALES_DASHBOARD_AI_INSIGHTS, { method: 'POST', body: JSON.stringify(summary) }));
      cache.current[key] = data;
      setInsights(data);
      // Save to history
      setHistory(prev => [{ dateRange: `${dateFrom} to ${dateTo}`, source, insights: data, timestamp: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
    } catch (err) { setError(err.message || 'Failed to generate insights'); }
    finally { setLoading(false); }
  }, [filteredData, getItemInfo, dateFrom, dateTo, source]);

  const renderInsights = (data, isDimmed = false, allowExpand = false) => {
    const items = data?.insights || (Array.isArray(data) ? data : null);
    if (!items) return (
      <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, whiteSpace: 'pre-wrap', opacity: isDimmed ? 0.6 : 1 }}>
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </Typography>
    );
    return items.map((insight, i) => {
      const sev = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;
      const text = insight.text || insight.message || insight.insight || insight.description || JSON.stringify(insight);
      const category = insight.category || insight.type || 'insight';
      const isExpanded = allowExpand && expandedInsight === i;
      return (
        <Box key={i}
          onClick={allowExpand ? () => setExpandedInsight(isExpanded ? null : i) : undefined}
          sx={{
            display: 'flex', gap: 1.5, py: isExpanded ? 2 : 1, px: isExpanded ? 1.5 : 0,
            opacity: isDimmed ? 0.6 : 1,
            borderBottom: i < items.length - 1 ? `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` : 'none',
            cursor: allowExpand ? 'pointer' : 'default',
            borderRadius: isExpanded ? 2 : 0,
            bgcolor: isExpanded ? (isDark ? '#0f172a' : '#f8fafc') : 'transparent',
            border: isExpanded ? `1px solid ${sev.color}40` : '1px solid transparent',
            transition: 'all 0.2s ease',
            my: isExpanded ? 0.5 : 0,
            '&:hover': allowExpand ? { bgcolor: isDark ? '#0f172a80' : '#f8fafc80' } : {},
          }}>
          <Box sx={{ color: sev.color, mt: 0.25, flexShrink: 0 }}>{sev.icon}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{
              color: isDark ? '#f1f5f9' : '#1e293b',
              fontSize: isExpanded ? 14 : 13,
              lineHeight: isExpanded ? 1.8 : 1.6,
              wordBreak: 'break-word',
              '& strong': { fontWeight: 700, color: isDark ? '#c7d2fe' : '#4338ca' },
              '& em': { fontStyle: 'italic' },
            }} dangerouslySetInnerHTML={{ __html: formatMarkdown(text) }} />
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
              <Chip label={category} size="small" sx={{ height: 20, fontSize: 9, bgcolor: sev.bg, color: sev.color, fontWeight: 600, textTransform: 'uppercase' }} />
              <Chip label={insight.severity || 'info'} size="small" sx={{ height: 20, fontSize: 9, bgcolor: sev.bg, color: sev.color, fontWeight: 600 }} />
              {allowExpand && (
                <Typography variant="caption" sx={{ ml: 'auto', fontSize: 9, color: isDark ? '#475569' : '#94a3b8' }}>
                  {isExpanded ? 'click to collapse' : 'click to expand'}
                </Typography>
              )}
            </Box>
            {isExpanded && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5 }}>
                <Tooltip title="Copy insight"><IconButton size="small" onClick={(e) => { e.stopPropagation(); copyTextToClipboard(text); }}
                  sx={{ width: 24, height: 24, color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: AI_COLOR } }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton></Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Paper sx={{ mb: 2, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 3, overflow: 'hidden', borderLeft: `3px solid ${AI_COLOR}` }}>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { bgcolor: isDark ? '#1e293b40' : '#f8fafc' } }}
        onClick={() => setOpen(!open)}>
        <AutoAwesomeIcon sx={{ color: AI_COLOR, fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, color: isDark ? '#f1f5f9' : '#0f172a' }}>
          AI Sales Insights
        </Typography>
        <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); analyze(); }} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: 11, bgcolor: AI_COLOR, '&:hover': { bgcolor: '#7c3aed' }, borderRadius: 2 }}>
          {insights ? 'Re-Analyze' : 'Analyze'}
        </Button>
        {history.length > 1 && (
          <Chip label={`${history.length} past`} size="small" onClick={(e) => { e.stopPropagation(); setShowHistory(!showHistory); }}
            sx={{ height: 22, fontSize: 10, bgcolor: isDark ? '#1e293b' : '#f1f5f9', cursor: 'pointer', '&:hover': { bgcolor: isDark ? '#334155' : '#e2e8f0' } }} />
        )}
        {insights && (
          <Tooltip title="Maximize"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setMaximized(true); }}
            sx={{ color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: AI_COLOR } }}>
            <OpenInFullIcon sx={{ fontSize: 16 }} />
          </IconButton></Tooltip>
        )}
        <IconButton size="small">{open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}</IconButton>
      </Box>
      {/* Maximized Dialog */}
      <Dialog open={maximized} onClose={() => setMaximized(false)} maxWidth={false} fullWidth
        PaperProps={{ sx: { width: '90vw', maxWidth: '90vw', height: '85vh', maxHeight: '85vh', borderRadius: 3, bgcolor: isDark ? '#0f172a' : '#fff' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
          <AutoAwesomeIcon sx={{ color: AI_COLOR, fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: isDark ? '#f1f5f9' : '#0f172a' }}>AI Sales Insights</Typography>
          <Button size="small" variant="contained" onClick={analyze} disabled={loading}
            startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', fontSize: 12, bgcolor: AI_COLOR, '&:hover': { bgcolor: '#7c3aed' }, borderRadius: 2 }}>
            {insights ? 'Re-Analyze' : 'Analyze'}
          </Button>
          <Tooltip title="Close"><IconButton onClick={() => setMaximized(false)} sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton></Tooltip>
        </Box>
        <DialogContent sx={{ p: 3, overflow: 'auto' }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {insights && renderInsights(insights, false, true)}
          {!insights && !loading && (
            <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8', py: 2, fontSize: 14 }}>
              Click "Analyze" to generate AI-powered insights from your current data.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {/* Current insights */}
          {insights && renderInsights(insights, false, true)}
          {!insights && !loading && (
            <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8', py: 1, fontSize: 12 }}>
              Click "Analyze" to generate AI-powered insights from your current data.
            </Typography>
          )}
          {/* History */}
          {showHistory && history.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
              <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase', fontWeight: 600, mb: 1, display: 'block' }}>
                Previous Analyses
              </Typography>
              {history.slice(1).map((h, i) => (
                <Box key={i} sx={{ mb: 2, p: 1.5, bgcolor: isDark ? '#0f172a' : '#f8fafc', borderRadius: 2, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <Chip label={h.dateRange} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 500 }} />
                    <Chip label={h.source} size="small" sx={{ height: 20, fontSize: 9 }} />
                    <Typography variant="caption" sx={{ color: isDark ? '#475569' : '#94a3b8', fontSize: 9, ml: 'auto' }}>{h.timestamp}</Typography>
                    <Tooltip title="Restore this analysis"><IconButton size="small" onClick={() => setInsights(h.insights)} sx={{ width: 20, height: 20 }}>
                      <Typography sx={{ fontSize: 11 }}>↩</Typography>
                    </IconButton></Tooltip>
                  </Box>
                  {renderInsights(h.insights, true)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// =====================================================================
// Natural Language Query
// =====================================================================
export const NLQueryBar = ({ filteredData, getItemInfo, dateFrom, dateTo }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [deepMode, setDeepMode] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [conversation, setConversation] = useState([]); // Array of { role: 'user'|'ai', text, data, chartType, confidence, images, files, timestamp, isDeep }
  const [chatHistory, setChatHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem('salesAI_chatHistory') || '[]'); } catch { return []; }
  }); // Array of { id, title, messages, createdAt }
  const [activeChatId, setActiveChatId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const chatEndRef = useRef(null);

  // Save history to localStorage whenever it changes
  const saveHistory = (h) => {
    setChatHistory(h);
    try { localStorage.setItem('salesAI_chatHistory', JSON.stringify(h.map(c => ({
      ...c, messages: c.messages.map(m => ({ ...m, images: undefined, files: undefined })) // strip large data for storage
    })).slice(0, 50))); } catch {}
  };

  // Auto-save current conversation to history
  const saveCurrentChat = (msgs) => {
    if (msgs.length < 2) return; // need at least 1 Q&A pair
    const firstQ = msgs.find(m => m.role === 'user')?.text || 'Chat';
    const title = firstQ.length > 60 ? firstQ.substring(0, 60) + '...' : firstQ;
    const now = new Date().toLocaleString();
    if (activeChatId) {
      saveHistory(chatHistory.map(c => c.id === activeChatId ? { ...c, title, messages: msgs, updatedAt: now } : c));
    } else {
      const newId = Date.now().toString();
      setActiveChatId(newId);
      saveHistory([{ id: newId, title, messages: msgs, createdAt: now, updatedAt: now }, ...chatHistory]);
    }
  };

  const loadChat = (chat) => {
    setConversation(chat.messages);
    setActiveChatId(chat.id);
    setShowHistory(false);
  };

  const startNewChat = () => {
    setConversation([]);
    setActiveChatId(null);
  };

  const deleteChat = (id, e) => {
    e.stopPropagation();
    saveHistory(chatHistory.filter(c => c.id !== id));
    if (activeChatId === id) { setConversation([]); setActiveChatId(null); }
  };

  const downloadBase64File = (base64, fileName, mimeType) => {
    const byteChars = atob(base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArray], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  };

  const ask = async () => {
    if (!question.trim()) return;
    const q = question.trim();
    setQuestion('');
    const isDeep = deepMode;
    setConversation(prev => [...prev, { role: 'user', text: isDeep ? `[Deep Analysis] ${q}` : q, timestamp: new Date().toLocaleTimeString() }]);
    setLoading(true);
    try {
      const ctx = buildQueryContext(filteredData, getItemInfo, dateFrom, dateTo);
      if (isDeep) {
        // Deep Analysis mode — code execution
        let data = await ensureParsed(await apiCall(API_ENDPOINTS.SALES_DASHBOARD_AI_DEEP_ANALYZE, {
          method: 'POST', body: JSON.stringify({ ...ctx, question: q }),
        }));
        const answerText = data?.answer || (typeof data === 'string' ? data : JSON.stringify(data));
        setConversation(prev => [...prev, {
          role: 'ai', text: answerText, images: data?.images || [], files: data?.files || [],
          confidence: 'high', isDeep: true, timestamp: new Date().toLocaleTimeString()
        }]);
      } else {
        // Standard query mode
        const recentHistory = conversation.slice(-6).map(m => `${m.role === 'user' ? 'User' : 'AI'}: ${m.text}`).join('\n');
        let data = await ensureParsed(await apiCall(API_ENDPOINTS.SALES_DASHBOARD_AI_QUERY, {
          method: 'POST', body: JSON.stringify({ ...ctx, question: q, conversationHistory: recentHistory }),
        }));
        const answerText = data?.answer || (typeof data === 'string' ? data : JSON.stringify(data));
        setConversation(prev => [...prev, { role: 'ai', text: answerText, data: data?.data, chartType: data?.chartType, confidence: data?.confidence, timestamp: new Date().toLocaleTimeString() }]);
      }
    } catch (err) {
      setConversation(prev => [...prev, { role: 'ai', text: err.message || 'Failed to get answer', confidence: 'low', timestamp: new Date().toLocaleTimeString() }]);
    }
    finally {
      setLoading(false);
      // Save conversation to history after each response
      setTimeout(() => {
        setConversation(curr => { saveCurrentChat(curr); return curr; });
      }, 100);
    }
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const renderMessage = (msg, i) => {
    if (msg.role === 'user') {
      return (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Box sx={{ bgcolor: AI_COLOR, color: '#fff', px: 2, py: 1, borderRadius: '16px 16px 4px 16px', maxWidth: '80%', fontSize: 13 }}>
            {msg.text}
            <Typography sx={{ fontSize: 9, opacity: 0.6, mt: 0.25, textAlign: 'right' }}>{msg.timestamp}</Typography>
          </Box>
        </Box>
      );
    }
    return (
      <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
        <Box sx={{ maxWidth: '85%' }}>
          <Box sx={{ bgcolor: isDark ? '#1e293b' : '#f1f5f9', px: 2, py: 1.25, borderRadius: '16px 16px 16px 4px', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
            {msg.confidence && (
              <Chip label={msg.confidence} size="small" sx={{ height: 18, fontSize: 8, mb: 0.75, bgcolor: msg.confidence === 'high' ? '#10b98120' : msg.confidence === 'medium' ? '#f59e0b20' : '#ef444420', color: msg.confidence === 'high' ? '#10b981' : msg.confidence === 'medium' ? '#f59e0b' : '#ef4444', fontWeight: 600 }} />
            )}
            <Typography variant="body2" sx={{ color: isDark ? '#f1f5f9' : '#1e293b', lineHeight: 1.6, fontSize: 13, wordBreak: 'break-word', '& strong': { fontWeight: 700, color: isDark ? '#c7d2fe' : '#4338ca' }, '& em': { fontStyle: 'italic' } }} dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.text) }} />
            {/* ===== CHART/DATA RENDERING ===== */}
            {msg.data?.length > 0 && msg.chartType === 'table' && (
              <Box sx={{ mt: 1, borderRadius: 1, overflow: 'hidden', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                {msg.data.map((row, j) => (
                  <Box key={j} sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.5, bgcolor: j % 2 === 0 ? 'transparent' : (isDark ? '#0f172a' : '#fff') }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 11 }}>{row.label}</Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: 11 }}>{row.value}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'multi_table' && msg.data[0]?.columns && (() => {
              const cols = msg.data[0].columns;
              return (
                <Box sx={{ mt: 1, borderRadius: 1, overflow: 'auto', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                  <Box sx={{ display: 'flex', bgcolor: isDark ? '#0f172a' : '#f8fafc', borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                    {cols.map((col, ci) => (
                      <Typography key={ci} variant="caption" sx={{ flex: 1, px: 1, py: 0.5, fontWeight: 700, fontSize: 10, color: isDark ? '#94a3b8' : '#64748b', minWidth: 80 }}>{col}</Typography>
                    ))}
                  </Box>
                  {msg.data.map((row, j) => (
                    <Box key={j} sx={{ display: 'flex', bgcolor: j % 2 === 0 ? 'transparent' : (isDark ? '#0f172a' : '#fff') }}>
                      {cols.map((col, ci) => (
                        <Typography key={ci} variant="caption" sx={{ flex: 1, px: 1, py: 0.4, fontSize: 10, color: isDark ? '#e2e8f0' : '#1e293b', minWidth: 80, fontWeight: ci === 0 ? 600 : 400 }}>{row[col] ?? ''}</Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              );
            })()}
            {msg.data?.length > 0 && msg.chartType === 'bar' && (
              <Box sx={{ mt: 1, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={msg.data.map(d => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <YAxis tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                    <Bar dataKey="value" fill={AI_COLOR} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'horizontal_bar' && (
              <Box sx={{ mt: 1, height: Math.max(180, msg.data.length * 28) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={msg.data.map(d => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                    <XAxis type="number" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} width={100} />
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                    <Bar dataKey="value" fill={AI_COLOR} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'multi_series' && (() => {
              const keys = Object.keys(msg.data[0] || {}).filter(k => k !== 'name' && k !== 'label');
              return (
                <Box sx={{ mt: 1, height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={msg.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                      <YAxis tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                      <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      {keys.map((key, ki) => (
                        <Bar key={key} dataKey={key} fill={CHART_COLORS[ki % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              );
            })()}
            {msg.data?.length > 0 && msg.chartType === 'multi_line' && (() => {
              const keys = Object.keys(msg.data[0] || {}).filter(k => k !== 'name' && k !== 'label');
              return (
                <Box sx={{ mt: 1, height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={msg.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                      <YAxis tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                      <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      {keys.map((key, ki) => (
                        <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[ki % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              );
            })()}
            {msg.data?.length > 0 && msg.chartType === 'line' && (
              <Box sx={{ mt: 1, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={msg.data.map(d => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <YAxis tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                    <Line type="monotone" dataKey="value" stroke={AI_COLOR} strokeWidth={2} dot={{ r: 3, fill: AI_COLOR }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'area' && (
              <Box sx={{ mt: 1, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={msg.data.map(d => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <YAxis tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                    <Area type="monotone" dataKey="value" stroke={AI_COLOR} fill={AI_COLOR} fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'pie' && (() => {
              const pieData = msg.data.map(d => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }));
              return (
                <Box sx={{ mt: 1, height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: isDark ? '#64748b' : '#94a3b8' }} style={{ fontSize: 9 }}>
                        {pieData.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                      </Pie>
                      <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              );
            })()}
            {msg.data?.length > 0 && msg.chartType === 'scatter' && (
              <Box sx={{ mt: 1, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
                    <XAxis dataKey="x" name="x" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <YAxis dataKey="y" name="y" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                    <ZAxis dataKey="label" name="name" />
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} cursor={{ strokeDasharray: '3 3' }} formatter={(value, name, props) => {
                      if (name === 'name') return [props.payload.label, 'Name'];
                      return [value, name];
                    }} />
                    <Scatter data={msg.data} fill={AI_COLOR} />
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'treemap' && (
              <Box sx={{ mt: 1, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <Treemap data={msg.data.map(d => ({ name: d.label, size: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }))} dataKey="size" nameKey="name" stroke={isDark ? '#1e293b' : '#fff'} fill={AI_COLOR}>
                    {msg.data.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                  </Treemap>
                </ResponsiveContainer>
              </Box>
            )}
            {msg.data?.length > 0 && msg.chartType === 'radar' && (() => {
              const radarData = msg.data.map(d => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0 }));
              return (
                <Box sx={{ mt: 1, height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke={isDark ? '#334155' : '#e2e8f0'} />
                      <PolarAngleAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} />
                      <PolarRadiusAxis tick={{ fontSize: 8, fill: isDark ? '#64748b' : '#94a3b8' }} />
                      <Radar dataKey="value" stroke={AI_COLOR} fill={AI_COLOR} fillOpacity={0.2} strokeWidth={2} />
                      <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
              );
            })()}
            {msg.data?.length > 0 && msg.chartType === 'funnel' && (
              <Box sx={{ mt: 1, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <FunnelChart>
                    <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 11 }} />
                    <Funnel dataKey="value" data={msg.data.map((d, idx) => ({ name: d.label, value: parseFloat(String(d.value).replace(/[^0-9.-]/g, '')) || 0, fill: CHART_COLORS[idx % CHART_COLORS.length] }))} isAnimationActive>
                      <LabelList position="center" fill="#fff" style={{ fontSize: 10, fontWeight: 600 }} />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </Box>
            )}
            {/* ===== DEEP ANALYSIS: IMAGES ===== */}
            {msg.images?.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {msg.images.map((img, ii) => (
                  <Box key={ii} sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                    {img.title && <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, bgcolor: isDark ? '#0f172a' : '#f8fafc', fontWeight: 600, fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}><ImageIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />{img.title}</Typography>}
                    <img src={`data:${img.mediaType};base64,${img.base64}`} alt={img.title || 'Chart'} style={{ width: '100%', display: 'block' }} />
                  </Box>
                ))}
              </Box>
            )}
            {/* ===== DEEP ANALYSIS: FILES ===== */}
            {msg.files?.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {msg.files.map((file, fi) => (
                  <Box key={fi} onClick={() => downloadBase64File(file.base64, file.name, file.mimeType)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 1,
                      border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, cursor: 'pointer',
                      '&:hover': { bgcolor: isDark ? '#0f172a' : '#f8fafc', borderColor: AI_COLOR } }}>
                    <InsertDriveFileIcon sx={{ fontSize: 16, color: AI_COLOR }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, color: isDark ? '#e2e8f0' : '#1e293b', display: 'block' }}>{file.name}</Typography>
                      <Typography variant="caption" sx={{ fontSize: 9, color: isDark ? '#64748b' : '#94a3b8' }}>{(file.size / 1024).toFixed(1)} KB — Click to download</Typography>
                    </Box>
                    <FileDownloadIcon sx={{ fontSize: 14, color: isDark ? '#64748b' : '#94a3b8' }} />
                  </Box>
                ))}
              </Box>
            )}
            {/* ===== EXPORT BUTTONS ===== */}
            {(msg.data?.length > 0 || msg.images?.length > 0) && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
                {msg.data?.length > 0 && (
                  <Tooltip title="Download data as CSV"><IconButton size="small" onClick={() => exportToCSV(msg.data, msg.chartType, '')} sx={{ width: 22, height: 22, color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: AI_COLOR } }}>
                    <FileDownloadIcon sx={{ fontSize: 14 }} />
                  </IconButton></Tooltip>
                )}
                <Tooltip title="Copy answer"><IconButton size="small" onClick={() => copyTextToClipboard(msg.text)} sx={{ width: 22, height: 22, color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: AI_COLOR } }}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton></Tooltip>
              </Box>
            )}
            {msg.isDeep && <Chip label="Deep Analysis" size="small" icon={<CodeIcon sx={{ fontSize: 10 }} />} sx={{ mt: 0.5, height: 18, fontSize: 8, bgcolor: isDark ? '#1e1b4b' : '#eef2ff', color: AI_COLOR, fontWeight: 600, '& .MuiChip-icon': { color: AI_COLOR } }} />}
            <Typography sx={{ fontSize: 9, color: isDark ? '#475569' : '#94a3b8', mt: 0.25 }}>{msg.timestamp}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const historyPanel = (isFullscreen = false) => (
    showHistory && (
      <Box sx={{ borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, maxHeight: isFullscreen ? 300 : 200, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, bgcolor: isDark ? '#0f172a' : '#f8fafc' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', color: isDark ? '#64748b' : '#94a3b8', flex: 1 }}>
            Conversation History ({chatHistory.length})
          </Typography>
          <Button size="small" onClick={startNewChat} sx={{ textTransform: 'none', fontSize: 10, color: AI_COLOR, minWidth: 'auto' }}>
            + New Chat
          </Button>
        </Box>
        {chatHistory.length === 0 && (
          <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1.5, color: isDark ? '#475569' : '#94a3b8', fontSize: 11 }}>
            No saved conversations yet. Your chats will appear here.
          </Typography>
        )}
        {chatHistory.map((chat) => (
          <Box key={chat.id} onClick={() => loadChat(chat)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, cursor: 'pointer',
              bgcolor: activeChatId === chat.id ? (isDark ? '#1e1b4b' : '#ede9fe') : 'transparent',
              borderLeft: activeChatId === chat.id ? `3px solid ${AI_COLOR}` : '3px solid transparent',
              '&:hover': { bgcolor: isDark ? '#1e293b' : '#f1f5f9' },
            }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11, color: isDark ? '#e2e8f0' : '#1e293b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {chat.title}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 9, color: isDark ? '#475569' : '#94a3b8' }}>
                {chat.messages.length} messages — {chat.updatedAt || chat.createdAt}
              </Typography>
            </Box>
            <Tooltip title="Delete"><IconButton size="small" onClick={(e) => deleteChat(chat.id, e)}
              sx={{ width: 20, height: 20, color: isDark ? '#475569' : '#cbd5e1', '&:hover': { color: '#ef4444' } }}>
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton></Tooltip>
          </Box>
        ))}
      </Box>
    )
  );

  const chatContent = (isFullscreen = false) => (
    <>
      {historyPanel(isFullscreen)}
      {conversation.length > 0 && (
        <Box sx={{ maxHeight: isFullscreen ? 'calc(85vh - 140px)' : 400, overflowY: 'auto', px: 2, py: 1.5, flex: isFullscreen ? 1 : 'unset' }}>
          {conversation.map(renderMessage)}
          {loading && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <CircularProgress size={14} sx={{ color: AI_COLOR }} />
              <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 11 }}>{deepMode ? 'Running analysis & generating charts...' : 'Thinking...'}</Typography>
            </Box>
          )}
          <div ref={chatEndRef} />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.5, borderTop: conversation.length > 0 ? `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` : 'none' }}>
        <TextField fullWidth size="small" placeholder={deepMode ? "Ask for deep analysis with charts & Excel..." : (conversation.length > 0 ? "Ask a follow-up question..." : "Ask anything about your sales data...")}
          value={question} onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><AutoAwesomeIcon sx={{ color: deepMode ? '#7c3aed' : AI_COLOR, fontSize: 18 }} /></InputAdornment>,
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: isDark ? '#0f172a' : '#fff', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: deepMode ? '#7c3aed' : AI_COLOR } } }}
        />
        <Tooltip title={deepMode ? "Deep Analysis ON — generates charts & Excel files (slower)" : "Enable Deep Analysis — Python code execution for charts, Excel reports, statistical analysis"}>
          <IconButton onClick={() => setDeepMode(!deepMode)} size="small"
            sx={{ width: 40, height: 40, borderRadius: 2,
              bgcolor: deepMode ? (isDark ? '#2e1065' : '#ede9fe') : 'transparent',
              border: `1.5px solid ${deepMode ? '#7c3aed' : (isDark ? '#334155' : '#e2e8f0')}`,
              color: deepMode ? '#7c3aed' : (isDark ? '#64748b' : '#94a3b8'),
              '&:hover': { bgcolor: isDark ? '#1e1b4b' : '#f5f3ff', borderColor: '#7c3aed' } }}>
            <CodeIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Button variant="contained" onClick={ask} disabled={loading || !question.trim()}
          sx={{ minWidth: 44, height: 40, bgcolor: deepMode ? '#7c3aed' : AI_COLOR, '&:hover': { bgcolor: deepMode ? '#6d28d9' : '#7c3aed' }, borderRadius: 2 }}>
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
        </Button>
        <Tooltip title={showHistory ? "Hide history" : `Chat History (${chatHistory.length})`}>
          <IconButton size="small" onClick={() => setShowHistory(!showHistory)}
            sx={{ color: showHistory ? AI_COLOR : (isDark ? '#64748b' : '#94a3b8'), '&:hover': { color: AI_COLOR } }}>
            <SearchIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        {!isFullscreen && (
          <Tooltip title="Maximize chat">
            <IconButton size="small" onClick={() => setMaximized(true)}
              sx={{ color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: AI_COLOR } }}>
              <OpenInFullIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {conversation.length > 0 && (
          <Tooltip title="New conversation">
            <IconButton size="small" onClick={startNewChat} sx={{ color: isDark ? '#475569' : '#94a3b8', '&:hover': { color: AI_COLOR } }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );

  return (
    <>
      <Paper sx={{ mb: 2, borderRadius: 3, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, overflow: 'hidden' }}>
        {chatContent(false)}
      </Paper>
      {/* Maximized Dialog */}
      <Dialog open={maximized} onClose={() => setMaximized(false)} maxWidth={false} fullWidth
        PaperProps={{ sx: { width: '90vw', maxWidth: '90vw', height: '85vh', maxHeight: '85vh', borderRadius: 3, bgcolor: isDark ? '#0f172a' : '#fff', display: 'flex', flexDirection: 'column' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, flexShrink: 0 }}>
          <AutoAwesomeIcon sx={{ color: AI_COLOR, fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: isDark ? '#f1f5f9' : '#0f172a' }}>Sales Data Chat</Typography>
          <Tooltip title="Close"><IconButton onClick={() => setMaximized(false)} sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            <CloseFullscreenIcon sx={{ fontSize: 20 }} />
          </IconButton></Tooltip>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {chatContent(true)}
        </Box>
      </Dialog>
    </>
  );
};

// =====================================================================
// Demand Forecast Tab
// =====================================================================
export const ForecastTab = ({ filteredData, getItemInfo, dateFrom, dateTo }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forecastDays, setForecastDays] = useState(30);
  const cache = useRef({});

  const generate = async () => {
    const key = hashKey(`forecast_${filteredData.length}_${dateFrom}_${dateTo}_${forecastDays}`);
    if (cache.current[key]) { setForecast(cache.current[key]); return; }
    setLoading(true); setError(null);
    try {
      const summary = buildForecastSummary(filteredData, getItemInfo);
      let data = await ensureParsed(await apiCall(API_ENDPOINTS.SALES_DASHBOARD_AI_FORECAST, {
        method: 'POST', body: JSON.stringify({ ...summary, forecastDays }),
      }));
      cache.current[key] = data;
      setForecast(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const chartData = forecast ? [
    ...(buildForecastSummary(filteredData, getItemInfo).dailyHistory || []).map(d => ({ date: d.date.slice(5), actual: d.qty, predicted: null })),
    ...(forecast.forecast || []).map(d => ({ date: (d.date || '').slice(5), actual: null, predicted: d.predictedQty })),
  ] : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" value={forecastDays} exclusive onChange={(_, v) => v && setForecastDays(v)}>
          <ToggleButton value={30} sx={{ textTransform: 'none', fontSize: 12 }}>30 Days</ToggleButton>
          <ToggleButton value={60} sx={{ textTransform: 'none', fontSize: 12 }}>60 Days</ToggleButton>
          <ToggleButton value={90} sx={{ textTransform: 'none', fontSize: 12 }}>90 Days</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" onClick={generate} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <TrendingUpIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontSize: 12, bgcolor: AI_COLOR, '&:hover': { bgcolor: '#7c3aed' }, borderRadius: 2 }}>
          {loading ? 'Generating...' : 'Generate Forecast'}
        </Button>
        {forecast?.trend && (
          <Chip label={`Trend: ${forecast.trend}`} size="small" sx={{ fontWeight: 600, fontSize: 11,
            bgcolor: forecast.trend === 'growing' ? '#10b98120' : forecast.trend === 'declining' ? '#ef444420' : '#f59e0b20',
            color: forecast.trend === 'growing' ? '#10b981' : forecast.trend === 'declining' ? '#ef4444' : '#f59e0b' }} />
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {forecast && chartData.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1e293b' : '#e2e8f0'} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
              <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
              <RTooltip contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="actual" stroke={TOOL_COLORS.REPORTS.primary} strokeWidth={2} dot={false} name="Actual" />
              <Line type="monotone" dataKey="predicted" stroke={AI_COLOR} strokeWidth={2} strokeDasharray="6 3" dot={false} name="Predicted" />
            </LineChart>
          </ResponsiveContainer>
          {forecast.seasonalNotes && (
            <Typography variant="body2" sx={{ mt: 1.5, color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontStyle: 'italic' }}>
              {forecast.seasonalNotes}
            </Typography>
          )}
          {forecast.methodology && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: isDark ? '#475569' : '#cbd5e1', fontSize: 10 }}>
              Methodology: {forecast.methodology}
            </Typography>
          )}
        </Paper>
      )}
      {!forecast && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
          <TrendingUpIcon sx={{ fontSize: 48, color: isDark ? '#334155' : '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: isDark ? '#64748b' : '#94a3b8' }}>Select forecast period and click Generate</Typography>
        </Paper>
      )}
    </Box>
  );
};

// =====================================================================
// Customer Segmentation Tab
// =====================================================================
export const CustomerSegmentTab = ({ filteredData, dateFrom, dateTo }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [segments, setSegments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cache = useRef({});

  const analyze = async () => {
    const key = hashKey(`customers_${filteredData.length}_${dateFrom}_${dateTo}`);
    if (cache.current[key]) { setSegments(cache.current[key]); return; }
    setLoading(true); setError(null);
    try {
      const summary = buildCustomerSummary(filteredData, dateFrom, dateTo);
      let data = await ensureParsed(await apiCall(API_ENDPOINTS.SALES_DASHBOARD_AI_CUSTOMERS, { method: 'POST', body: JSON.stringify(summary) }));
      cache.current[key] = data;
      setSegments(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Button variant="contained" onClick={analyze} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PeopleIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', fontSize: 12, bgcolor: AI_COLOR, '&:hover': { bgcolor: '#7c3aed' }, borderRadius: 2 }}>
          {loading ? 'Analyzing...' : 'Analyze Customers'}
        </Button>
        {segments?.summary && Object.entries(segments.summary).map(([seg, count]) => (
          <Chip key={seg} label={`${seg.charAt(0).toUpperCase() + seg.slice(1)}: ${count}`} size="small"
            sx={{ fontWeight: 600, fontSize: 11, bgcolor: (SEGMENT_COLORS[seg.charAt(0).toUpperCase() + seg.slice(1)] || '#6b7280') + '20', color: SEGMENT_COLORS[seg.charAt(0).toUpperCase() + seg.slice(1)] || '#6b7280' }} />
        ))}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {segments?.keyInsight && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, borderLeft: `3px solid ${AI_COLOR}` }} icon={<AutoAwesomeIcon sx={{ color: AI_COLOR }} />}>
          {segments.keyInsight}
        </Alert>
      )}
      {segments?.segments && (
        <Grid container spacing={1.5}>
          {segments.segments.map((cust, i) => {
            const segColor = SEGMENT_COLORS[cust.segment] || '#6b7280';
            return (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderLeft: `3px solid ${segColor}`, borderRadius: 2,
                  '&:hover': { boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)' } }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: isDark ? '#f1f5f9' : '#0f172a', fontSize: 13, flex: 1 }}>
                        {cust.name}
                      </Typography>
                      <Chip label={cust.segment} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: segColor + '20', color: segColor }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 9, textTransform: 'uppercase' }}>Orders</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>{cust.orderCount}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 9, textTransform: 'uppercase' }}>Revenue</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>${(cust.totalAmount || 0).toLocaleString()}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 9 }}>Churn Risk</Typography>
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 9, color: cust.churnRisk > 60 ? '#ef4444' : cust.churnRisk > 30 ? '#f59e0b' : '#10b981' }}>{cust.churnRisk}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={cust.churnRisk} sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? '#1e293b' : '#e2e8f0',
                        '& .MuiLinearProgress-bar': { bgcolor: cust.churnRisk > 60 ? '#ef4444' : cust.churnRisk > 30 ? '#f59e0b' : '#10b981', borderRadius: 2 } }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 10, lineHeight: 1.4, display: 'block' }}>
                      {cust.recommendation}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      {/* Fallback: data came back but not in expected format */}
      {segments && !segments.segments && (
        <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
          <Typography variant="body2" sx={{ color: isDark ? '#f1f5f9' : '#1e293b', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {segments.rawText || segments.keyInsight || JSON.stringify(segments, null, 2)}
          </Typography>
        </Paper>
      )}
      {!segments && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
          <PeopleIcon sx={{ fontSize: 48, color: isDark ? '#334155' : '#cbd5e1', mb: 1 }} />
          <Typography sx={{ color: isDark ? '#64748b' : '#94a3b8' }}>Click "Analyze Customers" to segment your customer base</Typography>
        </Paper>
      )}
    </Box>
  );
};

// =====================================================================
// Recommendations Panel
// =====================================================================
export const RecommendationsPanel = ({ filteredData, getItemInfo, selectedSku, selectedCustomer, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);
  const cache = useRef({});

  const mode = selectedSku ? 'sku' : 'customer';
  const selected = selectedSku || selectedCustomer;

  const generate = async () => {
    if (!selected) return;
    const key = hashKey(`recs_${mode}_${selected}_${filteredData.length}`);
    if (cache.current[key]) { setRecs(cache.current[key]); return; }
    setLoading(true);
    try {
      const ctx = buildRecommendationContext(filteredData, getItemInfo, mode, selected);
      let data = await ensureParsed(await apiCall(API_ENDPOINTS.SALES_DASHBOARD_AI_RECOMMENDATIONS, { method: 'POST', body: JSON.stringify(ctx) }));
      cache.current[key] = data;
      setRecs(data);
    } catch { setRecs(null); }
    finally { setLoading(false); }
  };

  // Auto-generate on mount/selection change
  React.useEffect(() => { if (selected) { setRecs(null); generate(); } }, [selected]);

  if (!selected) return null;

  return (
    <Paper sx={{ mt: 1.5, p: 2, borderRadius: 2.5, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, borderLeft: `3px solid ${AI_COLOR}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <RecommendIcon sx={{ color: AI_COLOR, fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, color: isDark ? '#f1f5f9' : '#0f172a', fontSize: 13 }}>
          AI Recommendations for {selected}
        </Typography>
        {loading && <CircularProgress size={16} sx={{ color: AI_COLOR }} />}
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      {recs?.pattern && (
        <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, mb: 1.5, fontStyle: 'italic' }}>{recs.pattern}</Typography>
      )}
      {recs?.recommendations?.map((rec, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 0.75, borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` }}>
          <Chip label={rec.strength} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, flexShrink: 0,
            bgcolor: rec.strength === 'strong' ? '#10b98120' : rec.strength === 'moderate' ? '#f59e0b20' : '#6b728020',
            color: rec.strength === 'strong' ? '#10b981' : rec.strength === 'moderate' ? '#f59e0b' : '#6b7280' }} />
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12, color: isDark ? '#e2e8f0' : '#1e293b' }}>{rec.item}</Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }}>{rec.reason}</Typography>
          </Box>
        </Box>
      ))}
      {recs?.crossSellActions?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase', fontWeight: 600 }}>Suggested Actions</Typography>
          {recs.crossSellActions.map((action, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5, alignItems: 'flex-start' }}>
              <Typography sx={{ color: AI_COLOR, fontSize: 11, mt: 0.25 }}>{'>'}</Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#e2e8f0' : '#334155', fontSize: 11 }}>{action}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default { InsightsPanel, NLQueryBar, ForecastTab, CustomerSegmentTab, RecommendationsPanel };
